import { t } from "adapters/i18n";
import { LivestockWeightType } from "constants/livestock";
import type { Dispatch } from "react-redux";
import type { RootState } from "redux/reducers";
import herdService from "services/herdService";
import { getErrorMessage } from "shared/errorUtils";
import type { LivestockInfoFromFarm, Weight } from "shared/types";
import {
  type LivestockDispatchAction,
  exibirCarregando,
  listFarmWeightGainCurves,
  listLivestockInfosFromCorrals,
  listLivestockInfosFromFarm,
  notifyError,
} from "../actions";

/**
 * Fetch livestock infos for all corrals in the selected farm.
 * @remarks It should be called after fetching the corrals.
 */
export const fetchLivestockInfosFromCorrals =
  () =>
  async (
    dispatch: Dispatch<LivestockDispatchAction>,
    getState: () => RootState,
  ) => {
    try {
      dispatch(exibirCarregando(true));

      const { pasture } = getState();
      const corralIds = pasture.farmCorrals.map((item) => item.id);

      const infos = await herdService.listLivestockFromPasturesIds(corralIds);
      dispatch(listLivestockInfosFromCorrals(infos.data));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("livestock:fetch-livestock-info-from-pasture-error"),
      );

      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const fetchFarmWeightGainCurves =
  () =>
  async (
    dispatch: Dispatch<LivestockDispatchAction>,
    getState: () => RootState,
  ) => {
    try {
      dispatch(exibirCarregando(true));

      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const weightGainCurves = await herdService.listCurvesForFarm(
        fazenda.FazendaSelecionada,
      );

      const sortedWeightGainCurves = weightGainCurves.data.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      );

      dispatch(listFarmWeightGainCurves(sortedWeightGainCurves));
    } catch (error) {
      dispatch(listFarmWeightGainCurves([]));
      const message = getErrorMessage(error);
      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

/**
 * Fetch livestock infos for all pastures in the selected farm.
 * @remarks It should be called after fetching the farm categories and pastures.
 */
export const fetchLivestockInfosFromFarm =
  () =>
  async (
    dispatch: Dispatch<LivestockDispatchAction>,
    getState: () => RootState,
  ) => {
    try {
      dispatch(exibirCarregando(true));

      const { fazenda, category, pasture, lot } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const result = await herdService.listLivestockFromFarm(
        fazenda.FazendaSelecionada,
        { with: ["goal", "health-check", "weight"] },
      );

      const categoryMap = new Map(
        category.enrichedFarmCategories.map((item) => [item.id, item]),
      );
      const pastureMap = new Map(
        pasture.farmPastures.map((item) => [item.id, item]),
      );
      const retreatMap = new Map(
        pasture.farmRetreats.map((item) => [item.id, item]),
      );
      const goalsMap = new Map(lot.goals.map((item) => [item.id, item]));

      const getLivestockWeight = (
        weightMap: Map<LivestockWeightType, Weight>,
        type: LivestockWeightType,
      ) => {
        const livestockWeight = weightMap.get(type) ?? null;

        if (livestockWeight == null) {
          return null;
        }

        return {
          id: livestockWeight.id,
          weight: livestockWeight.weight,
          weightDate: livestockWeight.operationDate,
          createdAt: livestockWeight.createdAt,
        };
      };

      const livestockFromFarms = Object.values(result)
        .flat()
        .map<LivestockInfoFromFarm>((item) => {
          const weightMap = new Map(
            item.weights?.map((weight) => [
              weight.weightType as LivestockWeightType,
              weight,
            ]) ?? [],
          );

          const pasture = pastureMap.get(item.pastureId) ?? null;
          const retreat = retreatMap.get(pasture?.retreatId ?? -1) ?? null;

          return {
            animalCategory: categoryMap.get(item.animalCategoryId) ?? null,
            animalCategoryId: item.animalCategoryId,
            createdAt: item.createdAt,
            deletedAt: item.deletedAt ?? null,
            endDate: item.endDate,
            exitWeight: getLivestockWeight(weightMap, LivestockWeightType.Exit),
            farmId: fazenda.FazendaSelecionada as number,
            goalId: item.goal?.id ?? null,
            goal: goalsMap.get(item.goal?.id ?? -1) ?? null,
            goalWeight: getLivestockWeight(weightMap, LivestockWeightType.Goal),
            healthCheck: item.healthCheck ?? null,
            id: item.id,
            isDeleted: item.deletedAt != null,
            measuredWeight: getLivestockWeight(
              weightMap,
              LivestockWeightType.Measured,
            ),
            operationDate: item.operationDate,
            pasture,
            pastureId: item.pastureId,
            projectedWeight: getLivestockWeight(
              weightMap,
              LivestockWeightType.Projected,
            ),
            quantity: item.quantity,
            retreat,
            updatedAt: item.updatedAt ?? null,
            visualWeight: getLivestockWeight(
              weightMap,
              LivestockWeightType.Visual,
            ),
          };
        });

      dispatch(listLivestockInfosFromFarm(livestockFromFarms));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("livestock:fetch-livestock-from-farm-error"),
      );

      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };
